import { Suspense, lazy, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { GenericModal, Tabs } from 'src/common';
import { companiesApi } from 'src/api/companiesApi';
import { isRequestAbortedError } from 'src/utils/axios/isRequestAbortedError';
import useGenericStyles from 'src/styles/useGenericStyles';
import SimpleBreadCrumb from 'src/components/widgets/BreadCrumb/SimpleBreadCrumb';
import { CustomButton2 } from 'src/components/widgets/buttons/CustomButton2';
import { Pricebook } from './pricebook';
import {
  CLIENT_DETAILS_TABS,
  DEFAULT_CLIENT_DETAILS_TAB,
  getClientDetailsHref,
  isClientDetailsTab,
  type ClientDetailsTab,
} from './clientDetailsTabs';

const About = lazy(() =>
  import('./about/about').then((module) => ({ default: module.About })),
);

const Contacts = lazy(() =>
  import('./contacts/contacts').then((module) => ({
    default: module.Contacts,
  })),
);

const AddEditContact = lazy(() =>
  import('src/components/add-edit-contact').then((module) => ({
    default: module.AddEditContact,
  })),
);

export const TabContentLoader = (): JSX.Element => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px"
  >
    <Typography>Loading...</Typography>
  </Box>
);

export const fetchCompanyDetails = async ({
  companyId,
  setLoading,
}: {
  companyId: string;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  if (!companyId) {
    return null;
  }

  if (setLoading) setLoading(true);
  try {
    const response = await companiesApi.getCompanyById(companyId);
    const data = response?.data?.data || response?.data || response;
    return data || null;
  } catch (error) {
    if (!isRequestAbortedError(error)) {
      console.error('Error fetching company details:', error);
    }
    return null;
  } finally {
    if (setLoading) setLoading(false);
  }
};

export const fetchCompanyOverview = async ({ companyId }: { companyId: string }) => {
  try {
    const response = await companiesApi.getCompanyOverviewById(companyId);
    const data = response?.data?.data || response?.data || response;
    return data || null;
  } catch (error) {
    console.error('Error fetching company overview:', error);
    return null;
  }
};

export const CompanyDetails = (): JSX.Element => {
  const classes = useGenericStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams<{ companyId?: string; tab?: string }>();
  const companyId = params.companyId?.trim() || '';

  const [isAddEditContactOpen, setIsAddEditContactOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState<any>({});
  const [companyOverview, setCompanyOverview] = useState<any>(null);

  const requestedTab = isClientDetailsTab(params.tab) ? params.tab : null;
  const activeTab: ClientDetailsTab = requestedTab ?? DEFAULT_CLIENT_DETAILS_TAB;

  // Keep URL in sync: missing/invalid tab → default (contacts).
  useEffect(() => {
    if (!companyId) return;
    if (params.tab === activeTab) return;
    navigate(getClientDetailsHref(companyId, activeTab), { replace: true });
  }, [companyId, params.tab, activeTab, navigate]);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: string | number | undefined,
  ): void => {
    const nextTab = String(newValue ?? '');
    if (!companyId || !isClientDetailsTab(nextTab)) return;
    navigate(getClientDetailsHref(companyId, nextTab));
  };

  const handleContactClose = (): void => setIsAddEditContactOpen(false);

  const breadcrumbData = {
    links: [
      {
        name: 'Clients',
        to: '/clients',
      },
      {
        name: companyDetails?.name,
        to: companyDetails?.id
          ? getClientDetailsHref(String(companyDetails.id))
          : '',
      },
    ],
  };

  useEffect(() => {
    if (!companyId) return undefined;

    let cancelled = false;
    const loadCompanyData = async () => {
      const detailsData = await fetchCompanyDetails({ companyId, setLoading });
      const overviewData = await fetchCompanyOverview({ companyId });
      if (cancelled) return;
      if (detailsData) setCompanyDetails(detailsData);
      if (overviewData) setCompanyOverview(overviewData);
    };

    loadCompanyData();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const renderTabContent = (): JSX.Element | null => {
    switch (activeTab) {
      case 'contacts':
        return (
          <Suspense fallback={<TabContentLoader />}>
            <Contacts companyId={companyDetails?.id} />
          </Suspense>
        );
      case 'pricebook':
        return (
          <Suspense fallback={<TabContentLoader />}>
            <Pricebook organizationId={companyDetails?.id} />
          </Suspense>
        );
      case 'about':
        return (
          <Suspense fallback={<TabContentLoader />}>
            <About
              companyData={companyDetails}
              companyOverview={companyOverview}
              onCompanyUpdated={(updated) => {
                setCompanyDetails(updated);
              }}
            />
          </Suspense>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
      >
        <Typography variant="h5">Loading company details...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} className={classes.container}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box display="flex" flexDirection="column">
            <SimpleBreadCrumb data={breadcrumbData} />
            <Typography variant="h4" sx={{ marginTop: '10px' }}>
              {companyDetails?.name}
            </Typography>
          </Box>

          <Box display="flex" gap={1}>
            {activeTab === 'contacts' && (
              <CustomButton2
                backgroundColor={theme.palette.primary.main}
                className={classes.button}
                onClick={() => setIsAddEditContactOpen(true)}
              >
                <AddIcon
                  sx={{
                    marginRight: '5px',
                  }}
                />
                Add Contact
              </CustomButton2>
            )}
          </Box>
        </Box>
        <Tabs
          tabs={CLIENT_DETAILS_TABS}
          handleTabChange={handleTabChange}
          activeTab={activeTab}
          isTransform={false}
        />

        <Box sx={{ mt: 1 }}>{renderTabContent()}</Box>

        <GenericModal
          open={isAddEditContactOpen}
          onClose={handleContactClose}
          title="Add Contact"
        >
          <Suspense fallback={<TabContentLoader />}>
            <AddEditContact
              companyId={params?.companyId ?? ''}
              onClose={() => {
                setIsAddEditContactOpen(false);
              }}
            />
          </Suspense>
        </GenericModal>
      </Box>
    </Container>
  );
};
